import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import { Languages, Volume2, ChevronRight, Loader2 } from "lucide-react";

export default function LanguageLab() {
  const [data, setData] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => { api.get("/language-lab").then((r) => { setData(r.data); setLesson(r.data.lessons.find((l) => l.unlocked && !l.done) || r.data.lessons[0]); }); }, []);

  const pronounce = async (w) => {
    if (playing) return;
    const key = w.native || w.chahta;
    setPlaying(key);
    try {
      const r = await api.post("/tts", { text: w.phonetic, voice: "onyx" });
      const url = r.data.url.startsWith("http") ? r.data.url : `${API.replace(/\/api$/, "")}${r.data.url}`;
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      const safety = setTimeout(() => setPlaying(null), 15000);
      audio.onended = () => { clearTimeout(safety); setPlaying(null); };
      audio.onerror = () => { clearTimeout(safety); setPlaying(null); toast.error("Could not play audio"); };
      await audio.play();
    } catch (e) {
      setPlaying(null);
      toast.error("Audio unavailable");
    }
  };

  if (!data) return <Loader />;

  return (
    <div data-testid="module-language-lab">
      <PageHeader
        eyebrow={`Language Lab • ${data.language}`}
        title="Language Lab"
        subtitle="Learn from curated linguistic sources — tribal language programs, dictionaries and archival recordings. The ladder: Words → Phrases → Sentences."
      />

      {data.matched ? (
        <div className="mb-5 rounded-xl bg-[#F3EEFA] border border-[#59288E]/20 p-3 text-sm text-[#3F1E68] flex items-center gap-2" data-testid="language-tuned-banner">
          <Languages size={16} className="text-[#59288E] shrink-0" /> Tuned to your affiliation — <span className="font-semibold">{(data.matched_nation || "").split(" (")[0]}</span>. These phrases reflect the language of your derived nation.
        </div>
      ) : (
        <div className="mb-5 rounded-xl bg-[#FFF9E6] border border-[#D4AF37]/40 p-3 text-sm text-[#8F7220] flex items-center gap-2" data-testid="language-default-banner">
          <Languages size={16} className="text-[#C59B27]" /> Showing the default Choctaw set. Generate your <span className="font-semibold">Tribal Affiliation report</span> to tune this to your nation.
        </div>
      )}

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {data.ladder.map((stage) => (
          <React.Fragment key={stage}>
            <span className={`px-3 py-1.5 rounded-full text-xs font-mono ${stage === data.current_stage ? "bg-[#D4AF37] text-[#180D24] font-semibold" : "bg-white border border-[#E4DCD3] text-[#756D7E]"}`}>{stage}</span>
            {stage !== data.ladder[data.ladder.length - 1] && <ChevronRight size={14} className="text-[#9CA3AF]" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="p-5 lg:col-span-1">
          <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-3">Lessons</h3>
          <div className="space-y-2">
            {data.lessons.map((l) => (
              <button
                key={l.n}
                data-testid={`lesson-${l.n}`}
                onClick={() => setLesson(l)}
                className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors hover:border-[#D4AF37]
                  ${lesson?.n === l.n ? "border-[#D4AF37] bg-[#FFF9E6]" : "border-[#E4DCD3] bg-white"}`}
              >
                <Languages size={16} className="text-[#59288E]" />
                <span className="text-xs font-mono text-[#8F7220]">{String(l.n).padStart(2, "0")}</span>
                <span className="text-sm text-[#180D24]">{l.title}</span>
              </button>
            ))}
          </div>
        </Panel>

        <Panel className="p-6 lg:col-span-2">
          {lesson && (
            <>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-serif text-2xl font-semibold text-[#180D24]">Lesson {lesson.n}: {lesson.title}</h3>
              </div>
              <p className="text-[11px] text-[#756D7E] mb-5 italic">{data.dialect_note} Pronunciation audio is generated by ElevenLabs at a slower, learner-friendly pace based on the phonetic key — authorized native recordings can be added later.</p>
              {lesson.words.length === 0 ? (
                <div className="text-center py-12 text-[#756D7E] text-sm">No content in this lesson yet.</div>
              ) : (
                <div className="space-y-3">
                  {lesson.words.map((w) => {
                    const wkey = w.native || w.chahta;
                    return (
                    <div key={wkey} className="flex items-center gap-4 rounded-xl border border-[#E4DCD3] p-4 hover:border-[#D4AF37] transition-colors" data-testid="flashcard">
                      <button
                        onClick={() => pronounce(w)}
                        disabled={!!playing}
                        className="h-11 w-11 shrink-0 rounded-full bg-[#2A1442] grid place-items-center text-[#E7C965] hover:bg-[#482276] transition-colors disabled:opacity-70"
                        data-testid="pronounce-btn"
                      >
                        {playing === wkey ? <Loader2 size={18} className="animate-spin" /> : <Volume2 size={18} />}
                      </button>
                      <div className="flex-1">
                        <div className="font-serif text-xl font-semibold text-[#59288E]">{wkey}</div>
                        <div className="text-sm text-[#180D24]">{w.en}</div>
                      </div>
                      <div className="font-mono text-xs text-[#8F7220]">{w.phonetic}</div>
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
